<template>
    <div class="equipment-control-modal">
        <div class="modal-body">
            <div v-if="selectedMode && controlProperties.length > 0" class="ecm-sections">
                <section v-for="(prop, index) in controlProperties" :key="index" class="ecm-section">
                    <!-- Enum: 운전모드·가동상태 등 -->
                    <template v-if="prop.encodeType === 'Enum' && prop.encodeDesc">
                        <div :class="['ecm-enum-wrap', isBinaryEnum(prop) ? 'ecm-enum-wrap--inline' : '']">
                            <h3 class="ecm-section-heading">{{ prop.propName }}</h3>
                            <div class="ecm-enum-select-slot ecm-selectbutton-dashboard-tabs">
                                <SelectButton
                                    :value="getPropertyValue(index)"
                                    :options="getEnumSelectOptions(prop)"
                                    optionLabel="label"
                                    optionValue="value"
                                    @input="setPropertyValue(index, $event)"
                                />
                            </div>
                        </div>
                    </template>

                    <!-- Range / Number + encodeDesc -->
                    <template
                        v-else-if="
                            (prop.encodeType === 'Range' || prop.encodeType === 'Number') &&
                            prop.encodeDescValue != null &&
                            hasRangeMinMax(prop)
                        "
                    >
                        <div class="ecm-temp-head">
                            <h3 class="ecm-section-heading ecm-section-heading--inline">{{ prop.propName }}</h3>
                            <div class="ecm-value-chip" aria-live="polite">
                                <span class="ecm-value-chip-text">{{ getPropertyDisplayValue(prop, index) }}</span>
                            </div>
                        </div>
                        <div class="ecm-temp-row">
                            <button
                                type="button"
                                class="ecm-step-button"
                                :aria-label="$t('값 내리기')"
                                @click="adjustRangeProperty(index, prop, -1)"
                            >
                                <span class="ecm-step-icon" aria-hidden="true">−</span>
                            </button>
                            <Slider
                                class="ecm-temp-slider w-56"
                                :value="getSliderValue(index, prop)"
                                :min="getRangeBounds(prop).min"
                                :max="getRangeBounds(prop).max"
                                :step="1"
                                @input="updatePropertyValue(index, $event)"
                            />
                            <button
                                type="button"
                                class="ecm-step-button"
                                :aria-label="$t('값 올리기')"
                                @click="adjustRangeProperty(index, prop, 1)"
                            >
                                <span class="ecm-step-icon" aria-hidden="true">+</span>
                            </button>
                        </div>
                        <div class="ecm-range-ticks">
                            <span>{{ getRangeBounds(prop).min }}</span>
                            <span>{{ getRangeBounds(prop).max }}</span>
                        </div>
                    </template>

                    <!-- 기본 범위 (16–30) -->
                    <template v-else>
                        <div class="ecm-temp-head">
                            <h3 class="ecm-section-heading ecm-section-heading--inline">{{ prop.propName }}</h3>
                            <div class="ecm-value-chip" aria-live="polite">
                                <span class="ecm-value-chip-text">{{ getPropertyDisplayValue(prop, index) }}</span>
                            </div>
                        </div>
                        <div class="ecm-temp-row">
                            <button
                                type="button"
                                class="ecm-step-button"
                                :aria-label="$t('값 내리기')"
                                @click="adjustRangeProperty(index, prop, -1)"
                            >
                                <span class="ecm-step-icon" aria-hidden="true">−</span>
                            </button>
                            <Slider
                                class="ecm-temp-slider w-56"
                                :value="getSliderValue(index, prop)"
                                :min="16"
                                :max="30"
                                :step="1"
                                @input="updatePropertyValue(index, $event)"
                            />
                            <button
                                type="button"
                                class="ecm-step-button"
                                :aria-label="$t('값 올리기')"
                                @click="adjustRangeProperty(index, prop, 1)"
                            >
                                <span class="ecm-step-icon" aria-hidden="true">+</span>
                            </button>
                        </div>
                        <div class="ecm-range-ticks">
                            <span>16</span>
                            <span>30</span>
                        </div>
                    </template>
                </section>
            </div>
        </div>

        <div class="modal-footer">
            <button type="button" class="ecm-footer-btn ecm-footer-btn--cancel" @click="$emit('close')">
                {{ $t("취소") }}
            </button>
            <button
                type="button"
                class="ecm-footer-btn ecm-footer-btn--apply"
                @click="applyControl"
                :disabled="!selectedMode || isApplying"
            >
                {{ isApplying ? $t("적용 중...") : $t("제어 적용") }}
            </button>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import { mapState } from "vuex";
import SelectButton from "primevue/selectbutton";
import Slider from "primevue/slider";

export default {
    name: "EquipmentControlModal",
    components: {
        SelectButton,
        Slider,
    },
    props: {
        nodeInfo: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            controlData: null,
            selectedMode: null,
            controlProperties: [],
            propertyValues: [],
            currentEquipStatus: null,
            isApplying: false,
        };
    },
    computed: {
        ...mapState({
            units: (state) => state.units,
        }),
    },
    mounted() {
        this.loadControlData();
    },
    methods: {
        /**
         * API의 encodeDesc 문자열을 한 번만 객체/배열로 올려 encodeDescValue에 둔다.
         * 이후 화면·제어 로직은 encodeDescValue(원시 맵/배열 형태)만 사용한다.
         */
        hydrateEncodeDescOnProps(props) {
            return props.map((prop) => {
                const row = { ...prop, encodeDescValue: null };
                const raw = prop.encodeDesc;
                if (raw == null || raw === "") {
                    return row;
                }
                if (typeof raw === "object") {
                    row.encodeDescValue = raw;
                    return row;
                }
                if (typeof raw === "string") {
                    try {
                        row.encodeDescValue = JSON.parse(raw);
                    } catch {
                        row.encodeDescValue = null;
                    }
                }
                return row;
            });
        },

        hasRangeMinMax(prop) {
            const d = prop.encodeDescValue;
            return d != null && typeof d === "object" && d.min != null && d.max != null;
        },

        getEnumSelectOptions(prop) {
            const parsed = prop.encodeDescValue;
            if (!parsed) {
                return [];
            }
            if (Array.isArray(parsed)) {
                return parsed.map((label, value) => ({ label: String(label), value }));
            }
            if (typeof parsed === "object") {
                return Object.keys(parsed)
                    .map((key) => {
                        const label = parsed[key];
                        const num = Number(key);
                        const value = Number.isFinite(num) && !Number.isNaN(num) ? num : key;
                        return { label: String(label), value };
                    })
                    .sort((a, b) => {
                        const na = typeof a.value === "number" ? a.value : Number(a.value);
                        const nb = typeof b.value === "number" ? b.value : Number(b.value);
                        if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
                        return String(a.value).localeCompare(String(b.value));
                    });
            }
            return [];
        },

        isBinaryEnum(prop) {
            return this.getEnumSelectOptions(prop).length === 2;
        },

        getRangeBounds(prop) {
            if ((prop.encodeType === "Range" || prop.encodeType === "Number") && this.hasRangeMinMax(prop)) {
                const d = prop.encodeDescValue;
                return {
                    min: Number(d.min),
                    max: Number(d.max),
                };
            }
            return { min: 16, max: 30 };
        },

        resolveEnumDisplayLabel(prop, value) {
            const parsed = prop.encodeDescValue;
            if (parsed == null || value === null || value === undefined) {
                return null;
            }
            if (Array.isArray(parsed)) {
                return parsed[value] != null ? String(parsed[value]) : null;
            }
            if (typeof parsed === "object") {
                if (parsed[value] != null) {
                    return String(parsed[value]);
                }
                const sk = String(value);
                if (parsed[sk] != null) {
                    return String(parsed[sk]);
                }
            }
            return null;
        },

        getSliderValue(index, prop) {
            const raw = this.getPropertyValue(index);
            const { min, max } = this.getRangeBounds(prop);
            if (raw === null || raw === undefined || Number.isNaN(Number(raw))) {
                return min;
            }
            return Math.min(max, Math.max(min, Number(raw)));
        },

        adjustRangeProperty(index, prop, delta) {
            const { min, max } = this.getRangeBounds(prop);
            const next = this.getSliderValue(index, prop) + delta;
            const clamped = Math.min(max, Math.max(min, next));
            this.updatePropertyValue(index, clamped);
        },

        async loadControlData() {
            if (!this.nodeInfo?.equipType) return;

            try {
                const result = await backEndApi.equipTypes.getEquipType(this.nodeInfo.equipType, "Y");
                if (result.status === 200) {
                    this.controlData = result.data;
                    // 기본 제어 모드 자동 선택
                    if (this.controlData.ctrlModes && this.controlData.ctrlModes.length > 0) {
                        // this.selectedMode = this.controlData.ctrlModes[0].ctrlMode;
                        this.selectedMode = "Simple";
                        await this.loadCurrentEquipStatus();
                        this.loadControlProperties();
                    }
                } else {
                    this.alertWarning(this.$t("제어 데이터를 가져오는 데 실패했습니다."));
                }
            } catch (error) {
                console.error("Error loading control data:", error);
                this.alertWarning(this.$t("제어 데이터를 가져오는 데 실패했습니다."));
            }
        },

        async selectControlMode(mode) {
            this.selectedMode = mode;
            await this.loadCurrentEquipStatus();
            this.loadControlProperties();
        },

        async loadCurrentEquipStatus() {
            if (!this.nodeInfo?.sysNodeIdx || !this.nodeInfo?.equipType) return;

            try {
                const result = await backEndApi.sysNode.getIncludedEquipStatus(
                    this.nodeInfo.sysNodeIdx,
                    this.nodeInfo.equipType,
                );
                if (result.status === 200 && result.data && result.data.length > 0) {
                    this.currentEquipStatus = result.data[0];
                } else {
                    console.warn("No equip status data available");
                    this.currentEquipStatus = null;
                }
            } catch (error) {
                console.error("Error loading current equip status:", error);
                this.currentEquipStatus = null;
            }
        },

        loadControlProperties() {
            if (!this.selectedMode || !this.controlData) return;

            const findMode = this.controlData.ctrlModes?.find((item) => item.ctrlMode === this.selectedMode);
            if (findMode) {
                const ctrlPropList = this.controlData.ioProps.filter(
                    (prop) => prop.ioType === "Both" || prop.ioType === "Out",
                );
                const matchingProps = findMode.outProps
                    .map((outProp) => {
                        return ctrlPropList.find((ctrlProp) => ctrlProp.propCode === outProp.propCode);
                    })
                    .filter((element) => element);

                this.controlProperties = this.hydrateEncodeDescOnProps(matchingProps);

                // 현재 상태값으로 초기화
                this.propertyValues = this.controlProperties.map((prop) => {
                    if (this.currentEquipStatus && this.currentEquipStatus.ioProps) {
                        const currentProp = this.currentEquipStatus.ioProps.find(
                            (statusProp) => statusProp.propCode === prop.propCode,
                        );
                        return currentProp ? currentProp.ptVal : null;
                    }
                    return null;
                });
            }
        },

        setPropertyValue(index, value) {
            this.$set(this.propertyValues, index, value);
        },

        updatePropertyValue(index, value) {
            this.$set(this.propertyValues, index, parseFloat(value));
        },

        getPropertyValue(index) {
            const value = this.propertyValues[index];
            return value === null || value === undefined ? null : value;
        },
        getUnitText(unit) {
            let rtnText = "";
            if (unit) {
                rtnText = this.units.find((code) => code.value === unit)?.text || unit;
            }
            return rtnText;
        },

        getPropertyDisplayValue(prop, index) {
            const value = this.propertyValues[index];
            if (value === null || value === undefined) {
                return this.$t("설정 안됨");
            }

            if (prop.encodeType === "Enum") {
                const label = this.resolveEnumDisplayLabel(prop, value);
                if (label != null) {
                    return label;
                }
                return value;
            } else if (prop.encodeType === "Range" || prop.encodeType === "Number") {
                return `${value} ${this.getUnitText(prop.unit)}`;
            }

            return value;
        },

        async applyControl() {
            if (!this.nodeInfo || !this.selectedMode) {
                this.alertWarning(this.$t("제어 모드를 선택해주세요."));
                return;
            }

            this.isApplying = true;

            try {
                const ctrlCmd = {
                    sysNodeIdx: this.nodeInfo.sysNodeIdx,
                    equipType: this.nodeInfo.equipType,
                    ctrlMode: this.selectedMode,
                    expandSearch: "Y",
                    propList: this.controlProperties.map((prop, index) => ({
                        propCode: prop.propCode,
                        ptVal: this.propertyValues[index] || 0,
                    })),
                };

                console.log("제어 명령:", ctrlCmd);

                const result = await backEndApi.sysNode.sendGroupCtrlCmd(ctrlCmd);

                // API 응답 구조 안전하게 처리
                if (result.status == 200 && result.data[0].result === "Success") {
                    this.alertNoti(this.$t("제어가 성공적으로 처리되었습니다."));
                    this.$emit("close");
                    this.$emit("load-status");
                } else {
                    if (result.data?.errcode === "PermissionDenied") {
                        this.alertWarning(this.$t("제어 권한이 없습니다."));
                    } else {
                        this.alertWarning(this.$t("제어에 실패했습니다."));
                    }
                }
            } catch (error) {
                console.error("Error applying control:", error);
                this.alertWarning(this.$t("처리 중 오류가 발생했습니다."));
            } finally {
                this.isApplying = false;
            }
        },
    },
};
</script>

<style scoped>
.equipment-control-modal {
    max-width: 36rem;
    width: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 25px 50px -12px rgb(15 23 42 / 0.18);
}

.modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background: #fff;
}

.ecm-sections {
    display: flex;
    flex-direction: column;
    gap: 19px;
}

.ecm-section {
    display: flex;
    flex-direction: column;
}

.ecm-section-heading {
    margin: 0;
    padding-left: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
}

.ecm-section-heading--inline {
    padding-left: 0;
}

.ecm-enum-wrap {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ecm-enum-wrap--inline {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
}

.ecm-enum-wrap--inline .ecm-section-heading {
    padding-left: 4px;
    flex-shrink: 0;
}

.ecm-enum-select-slot {
    width: 100%;
    min-width: 0;
}

.ecm-enum-wrap--inline .ecm-enum-select-slot {
    width: auto;
    flex-shrink: 0;
}

.ecm-temp-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.ecm-value-chip-text {
    font-size: 15px;
    font-weight: 700;
}

.ecm-temp-row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 4px 4px 0;
}

.ecm-step-button {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #475569;
    cursor: pointer;
    box-shadow: 0 1px 2px rgb(15 23 42 / 0.06);
    transition:
        background 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease,
        transform 0.1s ease;
}

.ecm-step-button:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #334155;
}

.ecm-step-button:active {
    transform: scale(0.96);
}

.ecm-step-icon {
    font-size: 20px;
    font-weight: 600;
}

.ecm-temp-slider.w-56 {
    flex: 1;
    min-width: 0;
    width: 14rem;
    max-width: 100%;
}

.ecm-range-ticks {
    display: flex;
    justify-content: space-between;
    padding: 2px 52px 0;
    font-size: 10px;
    font-weight: 600;
    color: #94a3b8;
}

.modal-footer {
    display: flex;
    flex-direction: row;
    gap: 10px;
    padding: 16px 22px 20px;
    border-top: 1px solid #f1f5f9;
    background: #fafafa;
}

.ecm-footer-btn {
    border-radius: 12px;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
    border: none;
    padding: 12px;
    transition:
        background 0.15s ease,
        color 0.15s ease,
        box-shadow 0.15s ease,
        opacity 0.15s ease;
}

.ecm-footer-btn--cancel {
    flex: 1;
    background: #fff;
    color: #475569;
    border: 1px solid #cbd5e1;
}

.ecm-footer-btn--cancel:hover {
    background: #f8fafc;
}

.ecm-footer-btn--apply {
    flex: 2;
    background: #334155;
    color: #fff;
    box-shadow: 0 10px 24px -8px rgb(51 65 85 / 0.45);
}

.ecm-footer-btn--apply:hover:not(:disabled) {
    background: #1e293b;
}

.ecm-footer-btn--apply:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
}

/*
 * SelectButton → DataVisualization.vue 의 .dashboard-tab-wrapper / .dashboard-tab-item 톤 정렬
 * (PrimeVue DOM만 ::v-deep 로 덮어씀)
 */
.ecm-selectbutton-dashboard-tabs ::v-deep .p-selectbutton {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    background: #f8f9fa;
    border: 1px solid rgb(226 232 240 / 0.92);
    border-radius: 10px;
    padding: 5px;
}

.ecm-selectbutton-dashboard-tabs ::v-deep .p-selectbutton .p-button {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 7px 27px;
    background: transparent;
    border: 2px solid transparent;
    border-radius: 8px;
    color: #6c757d;
    font-weight: 500;
    box-shadow: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.ecm-selectbutton-dashboard-tabs ::v-deep .p-selectbutton .p-button .p-button-label {
    font-weight: 600;
    font-size: 12px;
    white-space: nowrap;
}

.ecm-selectbutton-dashboard-tabs ::v-deep .p-selectbutton .p-button:not(.p-disabled):not(.p-highlight):hover {
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
    border-color: transparent;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.ecm-selectbutton-dashboard-tabs
    ::v-deep
    .p-selectbutton
    .p-button:not(.p-disabled):not(.p-highlight):hover
    .p-button-label {
    color: #3b82f6;
}

.ecm-selectbutton-dashboard-tabs ::v-deep .p-selectbutton .p-button.p-highlight {
    background: #3b82f6;
    color: #fff;
    border-color: #3b82f6;
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.ecm-selectbutton-dashboard-tabs ::v-deep .p-selectbutton .p-button.p-highlight .p-button-label {
    color: #fff;
}

.ecm-selectbutton-dashboard-tabs ::v-deep .p-selectbutton .p-button.p-highlight:hover {
    background: #3b82f6;
    color: #fff;
    border-color: #3b82f6;
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.35);
}

.ecm-selectbutton-dashboard-tabs ::v-deep .p-selectbutton .p-button.p-highlight:hover .p-button-label {
    color: #fff;
}

/* PrimeVue Slider — 모달 내 슬라이더만 레이아웃·가독성 보조 */
.ecm-temp-row ::v-deep .p-slider {
    flex: 1;
    min-width: 0;
    height: 7px;
    background: #e2e8f0;
    border-radius: 6px;
    border: none;
}

.ecm-temp-row ::v-deep .p-slider .p-slider-range {
    background: #3b82f6;
    border-radius: 6px;
}

.ecm-temp-row ::v-deep .p-slider .p-slider-handle {
    width: 22px;
    height: 22px;
    margin-top: -11px;
    margin-left: -11px;
    background: #3b82f6;
    border: 2px solid #fff;
    box-shadow: 0 4px 10px rgb(15 23 42 / 0.15);
    transition:
        transform 0.12s ease,
        box-shadow 0.12s ease;
    border-radius: 999px;
}

.ecm-temp-row ::v-deep .p-slider:not(.p-disabled) .p-slider-handle:hover {
    transform: scale(1.06);
    box-shadow: 0 6px 14px rgb(15 23 42 / 0.18);
}

.ecm-temp-row ::v-deep .p-slider:not(.p-disabled) .p-slider-handle:focus {
    outline: none;
    box-shadow:
        0 0 0 2px #fff,
        0 0 0 4px rgb(59 130 246 / 0.35);
}
</style>
